/* eslint-disable no-param-reassign */
import { CaseReducer, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { cloneDeep } from 'lodash';
import {
  CalenderActivityMeta,
  ActivityRowData,
  CalenderIntegrationRowDataWithMeta,
} from './activity-table';
import { NON_PROJECT_ACTIVITY_ID } from './utils';

interface CalenderActivityState {
  data?: CalenderIntegrationRowDataWithMeta[];
}
const initialState: CalenderActivityState = { data: undefined };

const initialize: CaseReducer<
  CalenderActivityState,
  PayloadAction<CalenderActivityState['data']>
> = (state, action) => {
  state.data = action.payload?.map(([meta, rows]) => {
    return [
      meta,
      rows.map((row) => ({
        ...row,
        billable: true,
      })),
    ];
  });
};

const updateSimilarTask = <T extends keyof CalenderActivityMeta>(
  state: CalenderActivityState,
  eventId: string,
  key: T,
  data: ActivityRowData[T],
) => {
  const newState = cloneDeep(state);
  let eventName;

  newState.data?.forEach(([_meta, rows]) => {
    eventName = rows.find((r) => r.eventId === eventId)?.eventName || eventName;
  });
  newState.data?.forEach(([_meta, rows]) => {
    const recurringRows = rows.filter((r) => r.eventName === eventName);

    recurringRows.forEach((row) => {
      if (!row[key]) {
        row[key] = data;
      }
    });
  });

  return newState.data;
};

const getNewActivity = <T extends keyof CalenderActivityMeta>(
  state: CalenderActivityState,
  eventId: string,
  key: T,
  data: ActivityRowData[T],
) => {
  const newState = cloneDeep(state);
  let meta;
  let row;

  newState.data?.forEach(([_meta, rows]) => {
    row = rows.find((r) => r.eventId === eventId) || row;
    if (row) {
      meta = _meta;
    }
  });

  if (!meta || !row) {
    return state.data;
  }
  if (row.isMarked) {
    return state.data;
  }
  row[key] = data;

  return newState.data;
};

const updateActivity: CaseReducer<
  CalenderActivityState,
  PayloadAction<AtLeast<ActivityRowData, 'eventId'>>
> = (state, action) => {
  const { payload: data } = action;

  if (data.projects) {
    state.data = getNewActivity(state, data.eventId, 'projects', data.projects);
    state.data = updateSimilarTask(
      state,
      data.eventId,
      'projects',
      data.projects,
    );
  }
  if (data.projects?.value === NON_PROJECT_ACTIVITY_ID) {
    state.data = getNewActivity(state, data.eventId, 'billable', false);
  }
  if (data.category) {
    state.data = getNewActivity(state, data.eventId, 'category', data.category);
    state.data = updateSimilarTask(
      state,
      data.eventId,
      'category',
      data.category,
    );
  }
  if (typeof data.billable === 'boolean') {
    state.data = getNewActivity(state, data.eventId, 'billable', data.billable);
  }
  if (typeof data.isSelected === 'boolean') {
    state.data = getNewActivity(
      state,
      data.eventId,
      'isSelected',
      data.isSelected,
    );
  }
};

export const calenderActivitySlice = createSlice({
  initialState,
  name: 'calender-activity',
  reducers: {
    initialize,
    updateActivity,
  },
});

export const calenderActivityReducer = calenderActivitySlice.reducer;
export const calenderActivityActions = calenderActivitySlice.actions;
